// React and Router imports
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";

// Firebase related imports
import { useAuthState } from "react-firebase-hooks/auth";
import {
        auth,
        registerWithEmailAndPassword,
        showNotification,
} from "../../firebase.js";
import TurnstileWidget from "../shared/TurnstileWidget";

// Styles
import "../../css/Auth.css"

function Register() {

        const [email, setEmail] = useState("")
        const [password, setPassword] = useState("")
        const [name, setName] = useState("")
        const [uid, setUid] = useState("")
        const [adLevel, setAdLevel] = useState("")
        const [user, loading, error] = useAuthState(auth)
        const [isSubmitting, setIsSubmitting] = useState(false)
        const [turnstileToken, setTurnstileToken] = useState("")
        const [captchaResetSignal, setCaptchaResetSignal] = useState(0)
        const navigate = useNavigate()

        const { enhancedRightClickEnabled } = useQuickLinks();
        const contextMenuOptions = [
                { label: "Home", url: "/", onClick: () => navigate("/") },
                { label: "Login", url: "/login", onClick: () => navigate("/login") },
                { label: "Forgot Password", url: "/reset", onClick: () => navigate("/reset") }
        ];
        const { showContextMenu, ContextMenuComponent } = useContextMenu(contextMenuOptions);

        const handleSelectedAdLevel = (e) => { setAdLevel(e.target.value) }

        const register = async () => {
                if (!name) {
                        showNotification("Please enter name")
                        return;
                }
                if (!turnstileToken) {
                        showNotification("Please complete the captcha check before registering.")
                        return;
                }
                setIsSubmitting(true);
                try {
                        const result = await registerWithEmailAndPassword(name, email, password, turnstileToken)

                        // Check if registration failed
                        if (result && result.error) {
                                // Turnstile tokens are single-use — force a fresh one for the retry
                                setCaptchaResetSignal((n) => n + 1)
                                const errorCode = result.error.code;
                                const errorMessage = result.error.message;
                                
                                // Handle specific error types
                                if (errorCode === "auth/email-already-in-use") {
                                        showNotification("This email is already registered. Please try logging in or use a different email.");
                                } else {
                                        // Generic error message for other auth errors, with Firebase error details
                                        showNotification(`Registration failed. Please try again. (${errorMessage})`);
                                }
                                return;
                        }
                        
                        const newUserId = result;
                        setUid(newUserId)

                        // Wait for Firebase auth state to update and get a fresh token
                        const user = auth.currentUser;
                        if (!user) {
                                throw new Error("User not authenticated after registration");
                        }
                        
                        // Force refresh the token to make sure it's valid
                        const token = await user.getIdToken(true);
                        
                        const data = {
                                email: email,
                                name: name,
                                userId: newUserId,
                                adLevel: adLevel,
                        }

                        const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/users/create/", {
                                method: "POST",
                                headers: {
                                        "Content-Type": "application/json",
                                        "Authorization": `${token}`,
                                },
                                body: JSON.stringify(data)
                        })

                        if (response.ok) {
                                console.log("Successfully saved user data to database.")
                        } else {
                                const errorData = await response.json();
                                console.log("Failed to submit user data to backend:", errorData);
                                // Don't show error - auto-repair will handle this on next login
                                console.log("User will be auto-repaired on next login");
                        }
                        
                        // Always redirect to verification page since Firebase Auth succeeded
                        navigate("/verify-email");
                } catch (error) {
                        console.error("Error in submitting data: ", error)
                        // If Firebase Auth succeeded but backend failed, still proceed
                        // Auto-repair will handle creating the backend entry on next login
                        if (uid) {
                                console.log("Firebase registration succeeded, proceeding despite backend error");
                                navigate("/verify-email");
                        } else {
                                // Only show error if Firebase Auth itself failed
                                setCaptchaResetSignal((n) => n + 1);
                                showNotification("Registration failed. Please try again.");
                        }
                } finally {
                        setIsSubmitting(false);
                }
        }

        const handleSubmit = (e) => {
                e.preventDefault();
                register();
        };

        useEffect(() => {
                if (loading) {
                        return
                }
                if (user) {
                        navigate("/")
                }
        }, [user, loading])

        return (
                <div 
                        className="container-fluid"
                        style={{
                                padding: "0",
                                minHeight: "100vh",
                                backgroundColor: "#6b9ce7"
                        }}
                        onContextMenu={enhancedRightClickEnabled ? (e) => {
                                e.preventDefault();
                                showContextMenu(e.clientX, e.clientY);
                        } : undefined}
                >

                        <div className="notification"></div>

                        <div className="row" style={{ minHeight: "100vh" }}>

                                <div className="col-12 d-flex justify-content-center align-items-center">

                                        <div className="Auth__box">
                                                <h1 className="Auth__text-center">Register</h1>
                                                <form onSubmit={handleSubmit}>
                                                        <div className="Auth__input-container">
                                                                <input
                                                                        type="text"
                                                                        value={name}
                                                                        onChange={(e) => setName(e.target.value)}
                                                                        required
                                                                />
                                                                <label>Name</label>
                                                        </div>
                                                        <div className="Auth__input-container">
                                                                <input
                                                                        type="email"
                                                                        value={email}
                                                                        onChange={(e) => setEmail(e.target.value)}
                                                                        required
                                                                />
                                                                <label>E-mail Address</label>
                                                        </div>
                                                        <div className="Auth__input-container">
                                                                <input
                                                                        type="password"
                                                                        value={password}
                                                                        onChange={(e) => setPassword(e.target.value)}
                                                                        required
                                                                />
                                                                <label>Password</label>
                                                        </div>
                                                        <TurnstileWidget
                                                                onToken={setTurnstileToken}
                                                                resetSignal={captchaResetSignal}
                                                        />
                                                        <button
                                                                type="submit"
                                                                className="Auth__btn"
                                                                disabled={isSubmitting}
                                                                style={{ position: "relative" }}
                                                        >
                                                                {isSubmitting ? (
                                                                        <span className="spinner"></span>
                                                                ) : (
                                                                        "Register"
                                                                )}
                                                        </button>
                                                </form>
                                                
                                                <div style={{ paddingTop: "30px", color: "white" }}>
                                                        Already have an account? <Link to="/login">Login</Link> now.
                                                </div>
                                                <a href="/privacy-policy.pdf" target="_blank" rel="noopener noreferrer" style={{ color: "#3f66a0", textDecoration: "underline" }}>
                                                        Privacy Policy
                                                </a>
                                        </div>

                                </div>

                        </div>

                        {enhancedRightClickEnabled && ContextMenuComponent}

                </div>
        )

}

export default Register